import { createApi } from "@reduxjs/toolkit/query/react";
import { ChatsApiResponse } from "../interface";
import { baseQueryWithReauth } from "./baseQueryWithReauth";
import { formatMessage } from "../../utils";

export const chatApi = createApi({
  reducerPath: "chatApi",
  baseQuery: baseQueryWithReauth,
  tagTypes: ["ChatList", "Messages"],
  endpoints: (builder) => ({
    getConversationList: builder.query<ChatsApiResponse, Record<string, any>>({
      query: (params) => ({ url: "chat/new-conversation-list", params }),
      providesTags: (result) =>
        result?.newCnv?.docs
          ? [
              ...result.newCnv.docs.map(({ _id }: any) => ({ type: "ChatList" as const, id: _id })),
              { type: "ChatList", id: "LIST" },
            ]
          : [{ type: "ChatList", id: "LIST" }],
    }),

    getChatMessages: builder.query<
      {
        messages: any[];
        hasNextPage: boolean;
      },
      {
        chatId: string;
        page: number;
        limit?: number;
        userId: string;
      }
    >({
      query: ({ chatId, page, limit = 20 }) => ({
        url: "chat/get-messages",
        params: { chatId, page, limit },
      }),
      providesTags: (result, error, { chatId }) => [
        { type: "Messages", id: chatId },
      ],
      transformResponse: (response: any, meta, arg) => {
        const { page, userId } = arg;

        const formatted = response.messageList.map((msg: any) =>
          formatMessage(msg, userId)
        );

        return {
          messages: page === 1 ? formatted.reverse() : formatted.reverse(),
          hasNextPage: response.hasNextPage,
        };
      },
    }),

    sendMessage: builder.mutation<any, any>({
      query: (body) => ({
        url: "chat/send-message",
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, { chatId }) => [
        { type: "ChatList", id: "LIST" },
        { type: "Messages", id: chatId },
      ],
    }),

    sendMediaMessage: builder.mutation<any, FormData>({
      query: (formData) => ({
        url: "chat/send-message-media",
        method: "POST",
        body: formData,
      }),
      invalidatesTags: (result, error, formData) => {
        const chatId = formData.get("chatId");
        return [
          { type: "ChatList", id: "LIST" },
          { type: "Messages", id: chatId as string },
        ];
      },
    }),

    deleteMessage: builder.mutation<any, { messageId: string; chatId: string }>({
      query: ({ messageId }) => ({
        url: "chat/delete-message",
        method: "POST",
        body: { messageId },
      }),
      invalidatesTags: (result, error, { chatId }) => [
        { type: "ChatList", id: "LIST" },
        { type: "Messages", id: chatId },
      ],
    }),

    deleteConversation: builder.mutation<any, { chatId: string }>({
      query: ({ chatId }) => ({
        url: "chat/delete-conversation",
        method: "POST",
        body: { chatId },
      }),
      invalidatesTags: (result, error, { chatId }) => [
        { type: "ChatList", id: "LIST" },
        { type: "Messages", id: chatId },
      ],
    }),

    muteConversation: builder.mutation<any, { chatId: string; mute: string }>({
      query: ({ chatId, mute }) => ({
        url: "chat/muteConversation",
        params: { chatId, mute },
        method: "GET",
      }),
      invalidatesTags: (result, error, { chatId }) => [
        { type: "ChatList", id: "LIST" },
        { type: "ChatList", id: chatId },
      ],
    }),

    pinConversation: builder.mutation<any, { chatId: string; pin: string }>({
      query: ({ chatId, pin }) => ({
        url: "chat/pinConversation",
        params: { chatId, pin },
        method: "GET",
      }),
      invalidatesTags: (result, error, { chatId }) => [
        { type: "ChatList", id: "LIST" },
        { type: "ChatList", id: chatId },
      ],
    }),

    archiveConversation: builder.mutation<any, { chatId: string; archive: string }>({
      query: ({ chatId, archive }) => ({
        url: "chat/archiveConversation",
        params: { chatId, archive },
        method: "GET",
      }),
      invalidatesTags: (result, error, { chatId }) => [
        { type: "ChatList", id: "LIST" },
        { type: "ChatList", id: chatId },
      ],
    }),
  }),
});

export const {
  useGetConversationListQuery,
  useLazyGetConversationListQuery,
  useGetChatMessagesQuery,
  useLazyGetChatMessagesQuery,
  useSendMessageMutation,
  useSendMediaMessageMutation,
  useDeleteMessageMutation,
  useDeleteConversationMutation,
  useMuteConversationMutation,
  usePinConversationMutation,
  useArchiveConversationMutation,
} = chatApi;
